import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axios';
import { format, formatDistanceToNow } from 'date-fns';
import { useWebSocket } from '../contexts/WebSocketContext';
import {
  ArrowPathIcon,
  CloudArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ServerIcon,
  ClockIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';

// What each release component actually is on the box.
const COMPONENT_INFO = {
  controller: { name: 'Controller', detail: 'C++ drying control (dm-control)' },
  panel: { name: 'Panel', detail: 'Kiosk API + UI (dm-panel)' },
  agent: { name: 'IoT agent', detail: 'Cloud link + update monitor (dm-iot-agent)' },
  database: { name: 'Database schema', detail: 'Postgres schema snapshot, converged on install' },
  tools: { name: 'Installer', detail: 'dm-update, the on-box installer' }
};

// A DM510 reports two things: the agent on its box and the panel's own software,
// read from the panel over Modbus. Nothing on the panel can be updated from here.
const COMPONENT_INFO_510 = {
  agent: { name: 'Box agent', detail: 'Cloud link on the DM510 box (dm510-agent)' },
  panel: { name: 'DM510 panel', detail: 'Panel software version, read from the panel; not updatable' }
};

const isDM510Serial = (serial) => /^dm510-/i.test(String(serial || ''));

// How the kiosk-side software_update row states read to a human.
const PENDING_STATES = {
  available: { label: 'Offered', hint: 'Popup shown - waiting on the operator', classes: 'bg-blue-100 text-blue-700' },
  deferred: { label: 'Deferred', hint: 'Operator pressed Later - orange pill on the kiosk', classes: 'bg-amber-100 text-amber-700' },
  install_requested: { label: 'Install requested', hint: 'Handing off to the installer', classes: 'bg-indigo-100 text-indigo-700' },
  installing: { label: 'Installing', hint: 'dm-update is applying the release', classes: 'bg-indigo-100 text-indigo-700' },
  done: { label: 'Installed', hint: 'Stays on the kiosk until the operator taps OK', classes: 'bg-green-100 text-green-700' },
  failed: { label: 'Failed', hint: 'Previous software keeps running', classes: 'bg-red-100 text-red-700' }
};

const CHECK_REASONS = {
  startup: 'agent start',
  interval: 'scheduled',
  cloud_nudge: 'admin request',
  requested: 'admin request',
  manual: 'manual'
};

function formatUptime(seconds) {
  if (!Number.isFinite(seconds)) return null;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function safeAgo(iso) {
  const t = new Date(iso);
  if (!iso || isNaN(t.getTime())) return null;
  return formatDistanceToNow(t, { addSuffix: true });
}

const DeviceSoftwareManager = ({ deviceId }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [receivedAt, setReceivedAt] = useState(null);
  const [live, setLive] = useState(false); // report arrived over SSE this session
  const [checking, setChecking] = useState(false);
  const [notice, setNotice] = useState(null); // { tone: 'info' | 'warn' | 'error', text }
  const checkTimer = useRef(null);

  const { addSSEListener } = useWebSocket();

  // Last stored report - the agent re-sends one after every check (10 min
  // cadence on a connected box), so this is current, not archaeology.
  const fetchStoredReport = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/v1/software/device/${deviceId}`);
      if (response.data.success) {
        setReport(response.data.data.report);
        setReceivedAt(response.data.data.received_at);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Failed to load software report:', err);
        setNotice({ tone: 'error', text: 'Could not load the stored software report.' });
      }
      // 404 just means the device has never reported - the empty state covers it.
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchStoredReport();
  }, [fetchStoredReport]);

  // Fresh reports stream in over the shared SSE channel.
  useEffect(() => {
    if (!addSSEListener) return;

    const handleSSEMessage = (data) => {
      if (data.type === 'software_report' && data.deviceId === deviceId) {
        setReport(data.report);
        setReceivedAt(data.timestamp || new Date().toISOString());
        setLive(true);
        setChecking(false);
        setNotice(null);
        if (checkTimer.current) {
          clearTimeout(checkTimer.current);
          checkTimer.current = null;
        }
      }
    };

    const cleanup = addSSEListener(`DeviceSoftwareManager-${deviceId}`, handleSSEMessage);
    return () => {
      cleanup();
      if (checkTimer.current) clearTimeout(checkTimer.current);
    };
  }, [addSSEListener, deviceId]);

  const handleCheckNow = async () => {
    setChecking(true);
    setNotice(null);
    try {
      await axiosInstance.post(`/v1/software/device/${deviceId}/check-updates`);
      // The reply is the next software_report over SSE. If a release has to be
      // downloaded first, the report follows the staging - be patient.
      checkTimer.current = setTimeout(() => {
        setChecking(false);
        setNotice({
          tone: 'info',
          text: 'Check sent - no report back yet. Large downloads take a while; this view updates by itself when the report lands.'
        });
      }, 30000);
    } catch (err) {
      setChecking(false);
      const offline = err.response?.status === 409;
      setNotice({
        tone: offline ? 'warn' : 'error',
        text: err.response?.data?.error || 'Failed to send the update check.'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  const versions = report?.versions || {};
  const componentEntries = Object.entries(versions).filter(([key]) => key !== 'release');
  const pending = report?.pendingUpdate || null;
  const pendingState = pending ? (PENDING_STATES[pending.state] || {
    label: pending.state, hint: '', classes: 'bg-gray-100 text-gray-700'
  }) : null;
  const lastCheck = report?.lastCheck || null;
  const upToDate = !pending && lastCheck?.channel_version &&
    versions.release === lastCheck.channel_version;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Software</h3>
          <p className="text-sm text-gray-600 mt-1">
            What this panel runs, as the panel itself reports it. Installs are
            confirmed by the operator on the dryer's screen - from here you can
            only make it check its channel.
          </p>
        </div>
        <button
          onClick={handleCheckNow}
          disabled={checking}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {checking ? (
            <ArrowPathIcon className="w-4 h-4 animate-spin" />
          ) : (
            <CloudArrowDownIcon className="w-4 h-4" />
          )}
          {checking ? 'Waiting for report...' : 'Check for updates'}
        </button>
      </div>

      {/* Notice */}
      {notice && (
        <div className={`border rounded-lg p-4 flex items-start gap-3 ${
          notice.tone === 'error' ? 'bg-red-50 border-red-200' :
          notice.tone === 'warn' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
        }`}>
          {notice.tone === 'error' ? (
            <XCircleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          ) : notice.tone === 'warn' ? (
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          ) : (
            <InformationCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          )}
          <p className={`text-sm ${
            notice.tone === 'error' ? 'text-red-700' :
            notice.tone === 'warn' ? 'text-amber-700' : 'text-blue-700'
          }`}>{notice.text}</p>
        </div>
      )}

      {!report ? (
        /* No report yet */
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <ServerIcon className="w-8 h-8 text-gray-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900">No software report from this panel yet</p>
          <p className="text-sm text-gray-600 mt-1">
            A connected panel reports after every update check (every 10 minutes).
            Press "Check for updates" to ask for one now.
          </p>
        </div>
      ) : (
        <>
          {/* Release / channel summary */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-base font-medium text-gray-900">Installed release</h4>
              <div className="flex items-center gap-2">
                {upToDate && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                    Up to date
                  </span>
                )}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  report.channel === 'canary'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {report.channel === 'canary' ? 'canary ring' : `${report.channel} ring`}
                </span>
              </div>
            </div>
            <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-600">Release</p>
                <p className="text-2xl font-bold text-gray-900 font-mono">
                  {versions.release ? `V${versions.release}` : 'unknown'}
                </p>
                {!versions.release && (
                  <p className="text-xs text-amber-600 mt-1">
                    No installed.json on the box - it will offer the whole release
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-600">Last channel check</p>
                <p className="text-sm font-medium text-gray-900">
                  {lastCheck ? (safeAgo(lastCheck.at) || 'unknown') : 'none since agent start'}
                </p>
                {lastCheck && (
                  <p className="text-xs text-gray-600 mt-1">
                    {CHECK_REASONS[lastCheck.reason] || lastCheck.reason}
                    {lastCheck.channel_version && ` - channel at ${lastCheck.channel_version}`}
                    {lastCheck.changed?.length > 0 && ` - changed: ${lastCheck.changed.join(', ')}`}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-600">System</p>
                <p className="text-sm font-medium text-gray-900">{report.system?.hostname || 'unknown'}</p>
                <p className="text-xs text-gray-600 mt-1">
                  {report.system?.node && `node ${report.system.node}`}
                  {formatUptime(report.system?.uptime_s) && ` - up ${formatUptime(report.system.uptime_s)}`}
                </p>
              </div>
            </div>
          </div>

          {/* Pending update */}
          {pending && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h4 className="text-base font-medium text-gray-900">
                  Update {pending.version}
                </h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${pendingState.classes}`}>
                  {pendingState.label}
                </span>
              </div>
              <div className="px-6 py-4 space-y-3">
                {pendingState.hint && (
                  <p className="text-sm text-gray-600">{pendingState.hint}</p>
                )}
                {pending.impact && (
                  <p className="text-sm text-gray-600">
                    Impact: <span className="font-medium text-gray-900">{pending.impact}</span>
                    {pending.impact === 'controller' && ' - installing interrupts drying for about a minute'}
                  </p>
                )}
                {pending.state === 'installing' && (
                  <div>
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{pending.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(pending.progress || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
                {pending.state === 'failed' && pending.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-700">{pending.error}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Component versions */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-base font-medium text-gray-900">Components</h4>
              <p className="text-xs text-gray-600 mt-1">
                A component's version is the release it last changed in - releases are
                cumulative and installs only touch what actually changed.
              </p>
            </div>
            <div className="divide-y divide-gray-200">
              {componentEntries.length === 0 && (
                <p className="px-6 py-4 text-sm text-gray-600">No component list reported.</p>
              )}
              {componentEntries.map(([key, version]) => {
                const info = (isDM510Serial(deviceId) ? COMPONENT_INFO_510 : COMPONENT_INFO)[key] || { name: key, detail: '' };
                return (
                  <div key={key} className="px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CpuChipIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{info.name}</p>
                        {info.detail && <p className="text-xs text-gray-600">{info.detail}</p>}
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-mono">
                      {version || 'unknown'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Report age */}
          <div className="flex items-center justify-end gap-2 text-sm text-gray-600">
            <ClockIcon className="w-4 h-4" />
            <span>
              Report {safeAgo(receivedAt) || 'age unknown'}
              {receivedAt && !isNaN(new Date(receivedAt).getTime()) &&
                ` (${format(new Date(receivedAt), 'MMM d, h:mm:ss a')})`}
            </span>
            {live && (
              <span className="flex items-center gap-1 text-green-600">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                live
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DeviceSoftwareManager;
